"use client";

// Third Party
import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { cloneDeep, isEmpty, pick } from "lodash";
import { toast } from "react-toastify";

// Contexts & Hooks
import { useUser } from "@/contexts/user-context";
import { useEarliestDelivery } from "@/hooks/useEarliestDelivery";

// Components
import CartDrawer from "@/components/common/Cart";
import VendorConflictModal from "@/components/VendorConflictModal";

// Helpers & API
import { checkoutAxiosInstance } from "@/services/axios";
import { CartItem, mapLinesToProducts, mapProductsToLines, mergeAndMapLinesToProducts } from "@/utils/cart";
import { sendCartActionEvent } from "@/utils/analytics";
const CheckoutContext = createContext<CheckoutContextType>({
  products: [],
  setProducts: () => {},
  vendorDetails: null,
  setVendorDetails: () => {},
  availableDeliverySlots: null,
  isCartOpen: false,
  setIsCartOpen: () => {},
  addProduct: () => Promise.resolve(),
  lowCartValueFee: 0,
  setLowCartValueFee: () => {},
  checkoutId: null,
  setCheckoutId: () => {},
  vendorConflictCartItems: {
    pendingCartItems: [],
    currentCartItems: [],
    refreshCartOnNewCartSelection: false
  },
  setVendorConflictCartItems: () => {},
  isBuyNowAction: false,
  setIsBuyNowAction: () => {},
  clearCart: () => {},
  bulkRemoveProducts: () => Promise.resolve([]),
  updateProductQuantity: () => Promise.resolve(),
  checkoutAddress: null,
  setCheckoutAddress: () => {},
  updateCheckoutAddress: async () => {},
  checkoutData: null,
  setCheckoutData: () => {},
  isCheckoutInitialized: false,
  isCheckoutUpdating: false,
  isProductOutOfStock: () => false,
  checkStockAndVisibility: async () => ({
    outOfStockProducts: [],
    isVendorOffline: false
  }),
  changeDeliveryDate: async () => {},
  changeDeliverySlot: async () => {},
  updateCheckoutNote: async () => {},
  updateCheckoutCoupon: async () => {},
  resetCheckout: () => {}
});
export interface CheckoutData {
  delivery_method: string;
  distance_km: number;
  delivery_slot: string | null;
  delivery_date: string | null;
  special_instructions: string;
  shipping_charges: number;
  net_price: number;
  total_tax: number;
  coupon: string | null;
  gross_price: number;
  discount_amount: number;
  rapidshyp_delivery_date: string | null;
  address?: Record<string, unknown>;
  outOfStockProductIds?: Set<string>;
}
interface CheckoutContextType {
  products: CartItem[];
  setProducts: (products: CartItem[]) => void;
  vendorDetails: Record<string, unknown> | null;
  setVendorDetails: (vendorDetails: Record<string, unknown> | null) => void;
  availableDeliverySlots: Record<string, unknown> | null;
  isCartOpen: boolean;
  setIsCartOpen: (isCartOpen: boolean) => void;
  addProduct: ({
    product,
    isBuyNowAction
  }: {
    product: CartItem;
    isBuyNowAction?: boolean;
  }) => Promise<void>;
  lowCartValueFee: number;
  setLowCartValueFee: (lowCartValueFee: number) => void;
  checkoutId: string | null;
  setCheckoutId: (checkoutId: string | null) => void;
  vendorConflictCartItems: {
    pendingCartItems: CartItem[];
    currentCartItems: CartItem[];
    refreshCartOnNewCartSelection: boolean;
  };
  setVendorConflictCartItems: (vendorConflictCartItems: {
    pendingCartItems: CartItem[];
    currentCartItems: CartItem[];
    refreshCartOnNewCartSelection: boolean;
  }) => void;
  isBuyNowAction: boolean;
  setIsBuyNowAction: (isBuyNowAction: boolean) => void;
  clearCart: () => void;
  bulkRemoveProducts: ({
    productsToBeRemoved,
    isOpenCart
  }: {
    productsToBeRemoved: CartItem[];
    isOpenCart?: boolean;
  }) => Promise<CartItem[]>;
  updateProductQuantity: ({
    product,
    quantity,
    action
  }: {
    product: CartItem;
    quantity: number;
    action: "add" | "remove";
  }) => Promise<void>;
  checkoutAddress: Record<string, unknown> | null;
  setCheckoutAddress: (checkoutAddress: Record<string, unknown> | null) => void;
  updateCheckoutAddress: (address: Record<string, unknown> | null) => Promise<void>;
  checkoutData: CheckoutData | null;
  setCheckoutData: (checkoutData: CheckoutData | null | ((prev: CheckoutData | null) => CheckoutData | null)) => void;
  isCheckoutInitialized: boolean;
  isCheckoutUpdating: boolean;
  isProductOutOfStock: (uniqueId: string) => boolean;
  checkStockAndVisibility: () => Promise<{
    outOfStockProducts: Array<CartItem & {
      uniqueId: string;
    }>;
    isVendorOffline: boolean;
  }>;
  changeDeliveryDate: (date: string | null) => Promise<void>;
  changeDeliverySlot: (slotId: string) => Promise<void>;
  updateCheckoutNote: (note: string) => Promise<void>;
  updateCheckoutCoupon: (couponId: string | null) => Promise<void>;
  resetCheckout: () => void;
}
export const useCheckout = () => {
  return useContext(CheckoutContext);
};
const extractCheckoutData = (data: Record<string, unknown>): Partial<CheckoutData> => {
  if (!data) return {};
  const baseData = pick(data, ["delivery_method", "delivery_slot", "delivery_date", "special_instructions", "shipping_charges", "net_price", "total_tax", "gross_price", "discount_amount"]);
  return {
    ...baseData,
    distance_km: data.distance_km || 0,
    coupon: data.voucher_code || null,
    rapidshyp_delivery_date: data.delivery_details?.delivery_date || null
  };
};
export const CheckoutProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [products, setProducts] = useState<CartItem[]>([]);
  const [vendorDetails, setVendorDetails] = useState<Record<string, unknown> | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [lowCartValueFee, setLowCartValueFee] = useState(0);
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [vendorConflictCartItems, setVendorConflictCartItems] = useState<{
    pendingCartItems: CartItem[];
    currentCartItems: CartItem[];
    refreshCartOnNewCartSelection: boolean;
  }>({
    pendingCartItems: [],
    currentCartItems: [],
    refreshCartOnNewCartSelection: false
  });
  const [isBuyNowAction, setIsBuyNowAction] = useState(false);
  const [checkoutAddress, setCheckoutAddress] = useState(null);
  const [isCheckoutInitialized, setIsCheckoutInitialized] = useState(false);
  const [isCheckoutUpdating, setIsCheckoutUpdating] = useState(false);
  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);
  const activeQuantityRequestRef = useRef<AbortController | null>(null);
  const isInitializingRef = useRef<Promise<string | null> | null>(null);
  const {
    isAuthenticated,
    isAuthLoading
  } = useUser();
  const {
    data: availableDeliverySlots,
    isError
  } = useQuery({
    queryKey: ["available-delivery-slots", vendorDetails?.id, [...(products || [])].sort((a, b) => Number(a.product_id) - Number(b.product_id))?.toString()],
    queryFn: () => checkoutAxiosInstance.get(`/delivery-slots/?checkout_token=${checkoutId}`),
    enabled: !!checkoutId && !!vendorDetails?.id && products?.length > 0 && (checkoutData?.delivery_method === "delivery" && vendorDetails?.is_vendor_delivery_available || checkoutData?.delivery_method === "pickup"),
    select: data => data.data
  });
  const {
    earliestDeliveryDate
  } = useEarliestDelivery(vendorDetails?.id, undefined, availableDeliverySlots?.max_prep_hours);
  useEffect(() => {
    if (isError) {
      toast.error("Error fetching available delivery slots");
    }
  }, [isError]);
  const bulkProductsAdd = useCallback(async ({
    products: newProducts,
    deliverySlot,
    deliveryDate,
    addressId,
    checkoutToken
  }: {
    products: CartItem[];
    deliverySlot?: string | null;
    deliveryDate?: string | null;
    addressId?: string | number | null;
    checkoutToken?: string | null;
  }) => {
    try {
      if (isAuthenticated) {
        const activeToken = checkoutToken || checkoutId;
        if (!activeToken) {
          console.warn("No checkout token available for bulkProductsAdd");
          return;
        }
        const checkoutResponse = await checkoutAxiosInstance.patch(`/checkouts/${activeToken}/`, {
          vendor: newProducts[0]?.vendor_id,
          lines: mapProductsToLines(newProducts),
          delivery_slot: deliverySlot || null,
          delivery_date: deliveryDate || null,
          address: addressId || null
        });
        if (checkoutResponse?.data) {
          if (!checkoutId && checkoutResponse.data.token) {
            setCheckoutId(checkoutResponse.data.token);
          }
          setLowCartValueFee(checkoutResponse.data.low_cart_value_fee || 0);
          setCheckoutAddress(checkoutResponse.data.address || null);
          setCheckoutData((prev: CheckoutData | null) => ({
            ...prev,
            ...extractCheckoutData(checkoutResponse.data)
          }));
          if (checkoutResponse.data.lines?.[0]?.product?.vendor_details) {
            setVendorDetails(checkoutResponse.data.lines[0]?.product?.vendor_details);
          }
          localStorage.removeItem("tooffi_cart_items");
        }
      }
      if (!isAuthenticated) {
        localStorage.setItem("tooffi_cart_items", JSON.stringify({
          items: newProducts,
          expiry: new Date().getTime() + 1000 * 60 * 60 * 24 * 30 // 30 days
        }));
        if (newProducts.length > 0) {
          setVendorDetails(newProducts[0]!.vendor_details);
        } else {
          setVendorDetails(null);
        }
      }
      setProducts(newProducts);
    } catch (error) {
      toast.error("Something went wrong while adding products to the checkout.");
      console.error("Error adding products:", error);
      return;
    }
  }, [checkoutId, isAuthenticated]);
  const initializeCheckout = useCallback(async (): Promise<string | null> => {
    if (isInitializingRef.current) {
      return isInitializingRef.current;
    }
    let resolve!: (token: string | null) => void;
    isInitializingRef.current = new Promise<string | null>(res => {
      resolve = res;
    });
    try {
      if (isAuthenticated) {
        // check if checkout already exists for the user on backend
        const response = await checkoutAxiosInstance.get("/checkouts/");
        const existingCheckouts = response?.data;
        let offlineProducts: CartItem[] = [];
        try {
          const rawCart = localStorage.getItem("tooffi_cart_items");
          if (rawCart) {
            const cartItems = JSON.parse(rawCart);
            offlineProducts = cartItems.items || [];
          }
        } catch (e) {
          console.error("Corrupted cart data in localStorage, clearing:", e);
          localStorage.removeItem("tooffi_cart_items");
        }
        if (offlineProducts.length > 0) {
          setProducts(offlineProducts);
          setVendorDetails(offlineProducts[0]!.vendor_details);
        }
        if (existingCheckouts?.results && existingCheckouts.results.length > 0) {
          const existingCheckout = existingCheckouts.results[0];
          setCheckoutId(existingCheckout.token);
          setCheckoutAddress(existingCheckout.address);
          setVendorDetails(existingCheckout.lines?.[0]?.product?.vendor_details || null);
          if (offlineProducts.length > 0 && existingCheckout.lines?.length > 0 && offlineProducts[0]?.vendor_id !== existingCheckout.vendor) {
            const currentCartItems = mapLinesToProducts(existingCheckout.lines);
            setLowCartValueFee(existingCheckout.low_cart_value_fee || 0);
            setCheckoutData((prev_0: CheckoutData | null) => ({
              ...prev_0,
              ...extractCheckoutData(existingCheckout)
            }));
            setVendorConflictCartItems({
              pendingCartItems: offlineProducts,
              currentCartItems,
              refreshCartOnNewCartSelection: true
            });
            setIsCheckoutInitialized(true);
            resolve(existingCheckout.token);
            return existingCheckout.token;
          }
          const timeStamp = new Date().getTime();
          const combinedProducts = mergeAndMapLinesToProducts(offlineProducts, existingCheckout.lines || []).map((item: CartItem) => {
            if (item && !item.timeStamp) {
              return {
                ...item,
                timeStamp
              };
            }
            return item;
          }) as CartItem[];
          if (offlineProducts.length > 0) {
            await bulkProductsAdd({
              products: combinedProducts,
              addressId: existingCheckout.address?.id,
              checkoutToken: existingCheckout.token
            });
          }
          if (existingCheckout.lines?.length > 0 && !offlineProducts.length) {
            setCheckoutId(existingCheckout.token);
            setCheckoutAddress(existingCheckout.address);
            setVendorDetails(existingCheckout.lines[0]?.product?.vendor_details || null);
            setCheckoutData((prev_1: CheckoutData | null) => ({
              ...prev_1,
              ...extractCheckoutData(existingCheckout)
            }));
            setProducts(combinedProducts);
          }
          setIsCheckoutInitialized(true);
          resolve(existingCheckout.token);
          return existingCheckout.token;
        }

        // If offline/guest products exist, create a checkout session for them
        if (offlineProducts.length > 0) {
          const checkoutResponse_0 = await checkoutAxiosInstance.post("/checkouts/", {
            vendor: offlineProducts[0]?.vendor_id,
            lines: mapProductsToLines(offlineProducts),
            special_instructions: checkoutData?.special_instructions || ""
          });
          if (checkoutResponse_0?.data) {
            const newToken = checkoutResponse_0.data.token;
            if (newToken) {
              setCheckoutId(newToken);
            }
            setLowCartValueFee(checkoutResponse_0.data.low_cart_value_fee || 0);
            if (checkoutResponse_0.data.lines?.[0]?.product?.vendor_details) {
              setVendorDetails(checkoutResponse_0.data.lines[0]?.product?.vendor_details);
            }
            setCheckoutData((prev_2: CheckoutData | null) => ({
              ...prev_2,
              ...extractCheckoutData(checkoutResponse_0.data)
            }));
            localStorage.removeItem("tooffi_cart_items");
            setIsCheckoutInitialized(true);
            resolve(newToken || null);
            return newToken || null;
          }
        }

        // If no existing token on backend and no guest products, preserve any token newly set
        setIsCheckoutInitialized(true);
        resolve(null);
        return null;
      }
      if (!isAuthenticated) {
        try {
          const cartItems_0 = localStorage.getItem("tooffi_cart_items");
          if (cartItems_0) {
            const parsedCartItems = JSON.parse(cartItems_0);
            setProducts(parsedCartItems.items || []);
            if (parsedCartItems.items?.[0]?.vendor_details) {
              setVendorDetails(parsedCartItems.items[0].vendor_details);
            }
          }
        } catch (e_0) {
          console.error("Corrupted cart data in localStorage, clearing:", e_0);
          localStorage.removeItem("tooffi_cart_items");
        }
        setCheckoutId(null);
      }
      setIsCheckoutInitialized(true);
      resolve(null);
      return null;
    } catch (error_0) {
      console.error("Error initializing checkout:", error_0);
      setIsCheckoutInitialized(true);
      resolve(null);
      return null;
    } finally {
      isInitializingRef.current = null;
    }
  }, [isAuthenticated, checkoutData?.special_instructions, bulkProductsAdd]);
  const updateCheckoutCoupon = useCallback(async (couponCode: string | null) => {
    let activeToken_0 = checkoutId;
    if (!activeToken_0 && isAuthenticated) {
      activeToken_0 = await initializeCheckout();
    }
    if (!activeToken_0) {
      return;
    }
    try {
      const checkoutResponse_1 = couponCode ? await checkoutAxiosInstance.post(`/checkouts/${activeToken_0}/apply-voucher/`, {
        voucher_code: couponCode
      }) : await checkoutAxiosInstance.post(`/checkouts/${activeToken_0}/remove-voucher/`);
      if (checkoutResponse_1?.data?.checkout) {
        setCheckoutData((prev_3: CheckoutData | null) => ({
          ...prev_3,
          coupon: checkoutResponse_1.data.checkout.voucher_code,
          discount_amount: checkoutResponse_1.data.checkout.discount_amount,
          gross_price: checkoutResponse_1.data.checkout.gross_price
        }));
      }
    } catch (error_1) {
      console.error("Error updating checkout coupon:", error_1);
      toast.error("Error updating checkout coupon");
      return;
    }
  }, [checkoutId, isAuthenticated, initializeCheckout]);
  const addProduct = useCallback(async ({
    product,
    isBuyNowAction: isBuyNowAction_0 = false
  }: {
    product: CartItem;
    isBuyNowAction?: boolean;
  }) => {
    let existingProductIndex: number | null = null;
    const existingProduct = products.find((p: CartItem, index: number) => {
      if (p.uniqueId === product.uniqueId) {
        existingProductIndex = index;
        return true;
      }
      return false;
    });
    if (!existingProduct && products.length > 0 && vendorDetails?.id && vendorDetails?.id !== product.vendor_id) {
      setVendorConflictCartItems({
        pendingCartItems: [product],
        currentCartItems: products,
        refreshCartOnNewCartSelection: false
      });
      setIsBuyNowAction(isBuyNowAction_0);
      return;
    }
    let newProducts_0: CartItem[] = [];
    if (existingProductIndex !== null && existingProduct) {
      newProducts_0 = cloneDeep(products);
      newProducts_0[existingProductIndex!]!.count += product.count;
      newProducts_0[existingProductIndex!]!.final_price_calculated = Number(newProducts_0[existingProductIndex!]!.final_price_calculated) + Number(product.final_price_calculated);
      (newProducts_0[existingProductIndex!] as CartItem & {
        timeStamp: number;
      }).timeStamp = new Date().getTime();
    } else {
      newProducts_0 = [...products, {
        ...product,
        timeStamp: new Date().getTime()
      } as CartItem & {
        timeStamp: number;
      }];
    }
    const previousVendorDetails = vendorDetails;
    if (products?.length === 0) {
      setVendorDetails(product.vendor_details);
    }
    try {
      if (isAuthenticated) {
        let activeToken_1 = checkoutId;
        if (!activeToken_1) {
          activeToken_1 = await initializeCheckout();
        }
        let checkoutResponse_2;
        if (!activeToken_1) {
          // No existing token after init — create a new checkout session
          checkoutResponse_2 = await checkoutAxiosInstance.post("/checkouts/", {
            vendor: product.vendor_id,
            lines: mapProductsToLines(newProducts_0),
            special_instructions: checkoutData?.special_instructions || ""
          });
        } else {
          // Active token exists, update existing checkout session
          checkoutResponse_2 = await checkoutAxiosInstance.patch(`/checkouts/${activeToken_1}/`, {
            vendor: product.vendor_id,
            lines: mapProductsToLines(newProducts_0),
            delivery_slot: null,
            delivery_date: null
          });
        }
        if (checkoutResponse_2?.data) {
          if (checkoutResponse_2.data.token) {
            setCheckoutId(checkoutResponse_2.data.token);
          }
          setLowCartValueFee(checkoutResponse_2.data.low_cart_value_fee || 0);
          setCheckoutAddress(checkoutResponse_2.data.address || null);
          setCheckoutData((prev_4: CheckoutData | null) => ({
            ...prev_4,
            ...extractCheckoutData(checkoutResponse_2.data)
          }));
        }
      }
      if (!isAuthenticated) {
        localStorage.setItem("tooffi_cart_items", JSON.stringify({
          items: newProducts_0,
          expiry: new Date().getTime() + 1000 * 60 * 60 * 24 * 30 // 30 days
        }));
      }
      sendCartActionEvent({
        eventName: "add_to_cart",
        product,
        isAuthenticated
      });
      setProducts(newProducts_0);
      if (!isBuyNowAction_0) {
        setIsCartOpen(true);
      }
    } catch (error_2) {
      // Revert vendorDetails if it was optimistically set
      if (products?.length === 0) {
        setVendorDetails(previousVendorDetails);
      }
      console.error("Error adding product to cart:", error_2);
      toast.error("Error adding product to cart");
      return;
    }
  }, [products, vendorDetails, isAuthenticated, checkoutId, checkoutData?.special_instructions, initializeCheckout]);
  const resetCheckout = useCallback(() => {
    setProducts([]);
    setVendorDetails(null);
    setCheckoutId(null);
    setCheckoutData(null);
    setLowCartValueFee(0);
    setCheckoutAddress(null);
    localStorage.removeItem("tooffi_cart_items");
  }, []);
  const updateProductQuantity = useCallback(async ({
    product: product_0,
    quantity,
    action = "add"
  }: {
    product: CartItem;
    quantity: number;
    action: "add" | "remove";
  }): Promise<void> => {
    try {
      const newProducts_1 = products.map((p_0: CartItem) => {
        if (p_0.uniqueId === product_0.uniqueId) {
          const newCount = action === "add" ? p_0.count + quantity : p_0.count - quantity;
          return {
            ...p_0,
            count: newCount,
            final_price_calculated: newCount ? Number(p_0.final_price_calculated) / p_0.count * newCount : 0,
            timeStamp: new Date().getTime()
          };
        }
        return p_0;
      }).filter((p_1: CartItem) => p_1.count > 0);

      // Optimistic update for UI responsiveness
      setProducts(newProducts_1);
      if (isAuthenticated) {
        let activeToken_2 = checkoutId;
        if (!activeToken_2) {
          activeToken_2 = await initializeCheckout();
        }
        if (activeToken_2) {
          setIsCheckoutUpdating(true);
          if (activeQuantityRequestRef.current) {
            activeQuantityRequestRef.current.abort();
          }
          const abortController = new AbortController();
          activeQuantityRequestRef.current = abortController;
          try {
            const checkoutResponse = await checkoutAxiosInstance.patch(`/checkouts/${activeToken_2}/`, {
              lines: mapProductsToLines(newProducts_1),
              ...(newProducts_1.length > 0 ? {} : {
                address: null
              }),
              ...(newProducts_1.length !== products.length ? {
                delivery_slot: null,
                delivery_date: null
              } : {})
            }, {
              signal: abortController.signal
            });
            if (checkoutResponse?.data) {
              setLowCartValueFee(checkoutResponse.data.low_cart_value_fee || 0);
              setCheckoutData((prev: CheckoutData | null) => ({
                ...prev,
                ...extractCheckoutData(checkoutResponse.data),
                address: checkoutResponse.data.address
              }));
            }
          } finally {
            if (activeQuantityRequestRef.current === abortController) {
              setIsCheckoutUpdating(false);
            }
          }
        }
      }
      if (!isAuthenticated) {
        localStorage.setItem("tooffi_cart_items", JSON.stringify({
          items: newProducts_1,
          expiry: new Date().getTime() + 1000 * 60 * 60 * 24 * 30 // 30 days
        }));
      }
      sendCartActionEvent({
        eventName: action === "add" ? "add_to_cart" : "remove_from_cart",
        product: product_0,
        isAuthenticated
      });
      if (newProducts_1.length === 0) {
        resetCheckout();
        return;
      }
    } catch (error_3: unknown) {
      const err = error_3 as {
        name?: string;
        code?: string;
      };
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        return; // Suppress cancelled request errors
      }
      toast.error("Error updating product quantity");
      return;
    }
  }, [products, isAuthenticated, checkoutId, initializeCheckout, resetCheckout]);
  const bulkRemoveProducts = useCallback(async ({
    productsToBeRemoved,
    isOpenCart = true
  }: {
    productsToBeRemoved: CartItem[];
    isOpenCart?: boolean;
  }) => {
    let newProducts_2: CartItem[] = products;
    productsToBeRemoved.forEach((productToRemoved: CartItem) => {
      newProducts_2 = newProducts_2.filter((p_2: CartItem) => p_2.uniqueId !== productToRemoved.uniqueId);
    });
    try {
      if (isAuthenticated) {
        let activeToken_3 = checkoutId;
        if (!activeToken_3) {
          activeToken_3 = await initializeCheckout();
        }
        if (activeToken_3) {
          const checkoutResponse_3 = await checkoutAxiosInstance.patch(`/checkouts/${activeToken_3}/`, {
            lines: mapProductsToLines(newProducts_2),
            vendor: newProducts_2[0]?.vendor_id,
            delivery_slot: null,
            delivery_date: null,
            ...(newProducts_2.length > 0 ? {} : {
              address: null
            })
          });
          if (checkoutResponse_3?.data) {
            setLowCartValueFee(checkoutResponse_3.data.low_cart_value_fee || 0);
            setCheckoutAddress(checkoutResponse_3.data.address || null);
            setCheckoutData((prev_5: CheckoutData | null) => ({
              ...prev_5,
              ...extractCheckoutData(checkoutResponse_3.data)
            }));
          }
        }
      }
      if (!isAuthenticated) {
        localStorage.setItem("tooffi_cart_items", JSON.stringify({
          items: newProducts_2,
          expiry: new Date().getTime() + 1000 * 60 * 60 * 24 * 30
        }) // 30 days
        );
      }
      if (newProducts_2.length === 0) {
        resetCheckout();
      }
      setProducts(newProducts_2);
      productsToBeRemoved.forEach((product_1: CartItem) => {
        sendCartActionEvent({
          eventName: "remove_from_cart",
          product: product_1,
          isAuthenticated
        });
      });
      if (isOpenCart) {
        setIsCartOpen(true);
      }
      return newProducts_2;
    } catch (error_4) {
      console.error("Error removing products:", error_4);
      toast.error("Error removing products");
      return [];
    }
  }, [products, isAuthenticated, checkoutId, initializeCheckout, resetCheckout]);
  const clearCart = useCallback(async () => {
    try {
      if (isAuthenticated) {
        let activeToken_4 = checkoutId;
        if (!activeToken_4) {
          activeToken_4 = await initializeCheckout();
        }
        if (activeToken_4) {
          await checkoutAxiosInstance.patch(`/checkouts/${activeToken_4}/`, {
            lines: [],
            special_instructions: "",
            delivery_slot: null,
            delivery_date: null,
            address: null
          });
        }
      }
      products.forEach((product_2: CartItem) => {
        sendCartActionEvent({
          eventName: "remove_from_cart",
          product: product_2,
          isAuthenticated
        });
      });
      resetCheckout();
    } catch (error_5) {
      toast.error("Error clearing cart");
      return;
    }
  }, [isAuthenticated, checkoutId, initializeCheckout, products, resetCheckout]);
  const updateCheckoutAddress = useCallback(async (address: Record<string, unknown> | null) => {
    const previousAddress = checkoutAddress;

    // Always update local state first for UI consistency and speed
    setCheckoutAddress(address);
    let activeToken_5 = checkoutId;
    if (!activeToken_5 && isAuthenticated) {
      // If checkoutId is missing but we're logged in, try to initialize it first
      activeToken_5 = await initializeCheckout();
    }
    if (!activeToken_5) {
      // If we still don't have a token (e.g. guest or failed init), we can't sync to server
      return;
    }
    const formattedEarliestDate = earliestDeliveryDate?.deliveryDate ? new Date(earliestDeliveryDate.deliveryDate.getTime() - earliestDeliveryDate.deliveryDate.getTimezoneOffset() * 60000).toISOString().split("T")[0] : null;
    try {
      const checkoutResponse_4 = await checkoutAxiosInstance.patch(`/checkouts/${activeToken_5}/`, {
        address: address ? address?.id : null,
        lines: mapProductsToLines(products),
        ...(formattedEarliestDate && checkoutData?.delivery_date !== formattedEarliestDate ? {
          delivery_date: formattedEarliestDate
        } : {})
      });
      if (checkoutResponse_4?.data) {
        setCheckoutAddress(checkoutResponse_4.data.address || null);
        setCheckoutData((prev_6: CheckoutData | null) => ({
          ...prev_6,
          ...extractCheckoutData(checkoutResponse_4.data)
        }));
      }
    } catch (error_6) {
      // Revert to previous address on error
      setCheckoutAddress(previousAddress);
      toast.error("Error updating checkout address");
      return;
    }
  }, [checkoutAddress, checkoutId, isAuthenticated, products, earliestDeliveryDate, checkoutData, initializeCheckout]);
  const checkStockAndVisibility = useCallback(async () => {
    let activeToken_6 = checkoutId;
    if (!activeToken_6 && isAuthenticated) {
      activeToken_6 = await initializeCheckout();
    }
    if (!activeToken_6) {
      return {
        outOfStockProducts: [],
        isVendorOffline: false
      };
    }
    try {
      // Fetch latest checkout data to check stock and visibility
      const response_0 = await checkoutAxiosInstance.get(`/checkouts/${activeToken_6}/`);
      const availableProducts = mapLinesToProducts(response_0?.data?.lines || []);
      const outOfStockProducts: Array<CartItem & {
        uniqueId: string;
      }> = [];
      const outOfStockIds = new Set<string>();
      let isVendorOffline = false;
      if (!isEmpty(availableProducts)) {
        if (availableProducts && Array.isArray(availableProducts)) {
          availableProducts.forEach((product_3: CartItem) => {
            if (product_3 && typeof product_3 === "object") {
              if (product_3.in_stock === false) {
                outOfStockProducts.push(product_3);
                outOfStockIds.add(product_3.uniqueId);
              }
              if (product_3.visible_in_listings === false) {
                isVendorOffline = true;
              }
            }
          });
        }
      }
      setCheckoutData((prev_7: CheckoutData | null) => ({
        ...prev_7,
        outOfStockProductIds: outOfStockIds
      }));
      return {
        outOfStockProducts: outOfStockProducts,
        isVendorOffline: isVendorOffline
      };
    } catch (error_7) {
      toast.error("Failed to check stock and visibility");
      return {
        outOfStockProducts: [],
        isVendorOffline: false
      };
    }
  }, [checkoutId, isAuthenticated, initializeCheckout]);
  const isProductOutOfStock = (uniqueId: string) => {
    return !!checkoutData?.outOfStockProductIds?.has(uniqueId);
  };
  const changeDeliveryDate = useCallback(async (date: string | null) => {
    let activeToken_7 = checkoutId;
    if (!activeToken_7 && isAuthenticated) {
      activeToken_7 = await initializeCheckout();
    }
    if (!activeToken_7) {
      return;
    }
    try {
      const checkoutResponse_5 = await checkoutAxiosInstance.patch(`/checkouts/${activeToken_7}/`, {
        lines: mapProductsToLines(products),
        delivery_date: date,
        delivery_slot: null
      });
      if (checkoutResponse_5?.data) {
        setCheckoutData((prev_8: CheckoutData | null) => ({
          ...prev_8,
          ...extractCheckoutData(checkoutResponse_5.data)
        }));
      }
    } catch (error_8) {
      console.error("Error changing delivery date:", error_8);
      toast.error("Error changing delivery date");
    }
  }, [checkoutId, isAuthenticated, initializeCheckout, products]);
  const changeDeliverySlot = useCallback(async (slotId: string) => {
    let activeToken_8 = checkoutId;
    if (!activeToken_8 && isAuthenticated) {
      activeToken_8 = await initializeCheckout();
    }
    if (!activeToken_8) {
      return;
    }
    try {
      const checkoutResponse_6 = await checkoutAxiosInstance.patch(`/checkouts/${activeToken_8}/`, {
        lines: mapProductsToLines(products),
        delivery_slot: slotId
      });
      if (checkoutResponse_6?.data) {
        setCheckoutData((prev_9: CheckoutData | null) => ({
          ...prev_9,
          ...extractCheckoutData(checkoutResponse_6.data)
        }));
      }
    } catch (error_9) {
      console.error("Error changing delivery slot:", error_9);
      toast.error("Error changing delivery slot");
    }
  }, [checkoutId, isAuthenticated, initializeCheckout, products]);
  const updateCheckoutNote = useCallback(async (note: string) => {
    let activeToken_9 = checkoutId;
    if (!activeToken_9 && isAuthenticated) {
      activeToken_9 = await initializeCheckout();
    }
    if (!activeToken_9) {
      return;
    }
    try {
      const checkoutResponse_7 = await checkoutAxiosInstance.patch(`/checkouts/${activeToken_9}/`, {
        lines: mapProductsToLines(products),
        special_instructions: note
      });
      if (checkoutResponse_7?.data) {
        setCheckoutData((prev_10: CheckoutData | null) => ({
          ...prev_10,
          ...extractCheckoutData(checkoutResponse_7.data)
        }));
      }
    } catch (error_10) {
      toast.error("Error updating checkout note");
      return;
    }
  }, [checkoutId, isAuthenticated, initializeCheckout, products]);
  useEffect(() => {
    if (!isAuthLoading) {
      initializeCheckout();
    }
  }, [isAuthLoading, isAuthenticated]);
  const value = useMemo(() => ({
    products,
    setProducts,
    vendorDetails,
    setVendorDetails,
    availableDeliverySlots,
    isCartOpen,
    setIsCartOpen,
    addProduct,
    lowCartValueFee,
    setLowCartValueFee,
    checkoutId,
    setCheckoutId,
    vendorConflictCartItems,
    setVendorConflictCartItems,
    clearCart,
    bulkRemoveProducts,
    updateProductQuantity,
    checkoutAddress,
    setCheckoutAddress,
    updateCheckoutAddress,
    checkoutData,
    setCheckoutData,
    isCheckoutInitialized,
    isCheckoutUpdating,
    resetCheckout,
    checkStockAndVisibility,
    isProductOutOfStock,
    isBuyNowAction,
    setIsBuyNowAction,
    changeDeliveryDate,
    changeDeliverySlot,
    updateCheckoutNote,
    updateCheckoutCoupon
  }), [products, vendorDetails, availableDeliverySlots, isCartOpen, lowCartValueFee, checkoutId, vendorConflictCartItems, checkoutAddress, checkoutData, isCheckoutInitialized, isCheckoutUpdating, isBuyNowAction, addProduct, clearCart, bulkRemoveProducts, updateProductQuantity, updateCheckoutAddress, resetCheckout, checkStockAndVisibility, changeDeliveryDate, changeDeliverySlot, updateCheckoutNote, updateCheckoutCoupon]);
  return <CheckoutContext.Provider value={value}>
      <CartDrawer />
      {vendorConflictCartItems.pendingCartItems.length > 0 && <VendorConflictModal pendingCartItems={vendorConflictCartItems.pendingCartItems} currentCartItems={vendorConflictCartItems.currentCartItems} isBuyNowAction={isBuyNowAction} bulkProductsAdd={bulkProductsAdd} setIsCartOpen={setIsCartOpen} setVendorConflictCartItems={setVendorConflictCartItems} refreshCartOnNewCartSelection={vendorConflictCartItems.refreshCartOnNewCartSelection} setProducts={setProducts} />}
      {children}
    </CheckoutContext.Provider>;
};